/**
 * @typedef {{ name: string; description: string; variantId: number|string; price: number; image: string|null; available?: boolean }} BagItem
 * @typedef {{ title: string; subtitle: string; variantId: number|string; price: number; image: string|null; available?: boolean }} ProductItem
 * @typedef {{
*   bags?: unknown;
*   products?: unknown;
*   kitPrice?: unknown;
*   ctaLabel?: unknown;
*   maxProducts?: unknown;
*   moneyFormat?: unknown;
*   currencyCode?: unknown;
*   strings?: Record<string, string>;
* }} BundleConfig
* @typedef {{ event: string } & Record<string, unknown>} DataLayerEvent
* @typedef {{ id: number|string; quantity: number; parent_id?: number|string; properties: { _bundle_id: string; _bundle_role: 'bag'|'product' } }} CartItem
*/

document.addEventListener('DOMContentLoaded', () => {
 const config = /** @type {{ BB_CONFIG?: BundleConfig; dataLayer?: DataLayerEvent[] }} */ (window).BB_CONFIG || {};
 const bags = Array.isArray(config.bags) ? /** @type {BagItem[]} */ (config.bags) : [];
 const products = Array.isArray(config.products) ? /** @type {ProductItem[]} */ (config.products) : [];
 const kitPrice = Number(config.kitPrice) || 45;
 const stringsBlock =
   config.strings && typeof config.strings === 'object' ? /** @type {Record<string, string>} */ (config.strings) : {};

 /**
  * @param {string} key
  * @returns {string}
  */
 function tr(key) {
   const value = stringsBlock[key];
   return typeof value === 'string' ? value : '';
 }

 /**
  * Fills `[[name]]` placeholders (same tokens as Liquid SSR).
  *
  * @param {string} template
  * @param {Record<string, string | number>} vars
  * @returns {string}
  */
 function interpolate(template, vars) {
   return template.replace(/\[\[(\w+)\]\]/g, (_, name) => {
     const v = vars[name];
     return v === undefined || v === null ? '' : String(v);
   });
 }

 const maxProducts = Math.max(1, Number(config.maxProducts) || 4);
 const resolvedCtaLabel =
   typeof config.ctaLabel === 'string' && config.ctaLabel.trim().length > 0
     ? config.ctaLabel.trim()
     : tr('cta_default');
 const bundleId = generateBundleId();

 const root = document.querySelector('[data-bb-root]');
 if (!(root instanceof HTMLElement) || !bags.length || !products.length) {
   return;
 }

 /** @type {number | null} */
 let selectedBag = null;
 /** @type {number[]} */
 let selectedProducts = [];
 let isSubmitting = false;

 const bagCards = Array.from(root.querySelectorAll('[data-bb-bag-card]'));
 const productCards = Array.from(root.querySelectorAll('[data-bb-product-card]'));
 /** @type {Record<1|2|3, HTMLElement | null>} */
 const stepNums = {
   1: toHTMLElement(root.querySelector('[data-bb-step-num="1"]')),
   2: toHTMLElement(root.querySelector('[data-bb-step-num="2"]')),
   3: toHTMLElement(root.querySelector('[data-bb-step-num="3"]'))
 };
 /** @type {Record<1|2|3, HTMLElement | null>} */
 const stepLabels = {
   1: toHTMLElement(root.querySelector('[data-bb-step-label="1"]')),
   2: toHTMLElement(root.querySelector('[data-bb-step-label="2"]')),
   3: toHTMLElement(root.querySelector('[data-bb-step-label="3"]'))
 };

 const productStepNumber = toHTMLElement(root.querySelector('[data-bb-product-step-number]'));
 const progressText = toHTMLElement(root.querySelector('[data-bb-progress-text]'));
 const progressCount = toHTMLElement(root.querySelector('[data-bb-progress-count]'));
 const progressFill = toHTMLElement(root.querySelector('[data-bb-progress-fill]'));
 const noBagNotice = toHTMLElement(root.querySelector('[data-bb-no-bag-notice]'));
 const productGrid = toHTMLElement(root.querySelector('[data-bb-product-grid]'));
 const selectedBagEl = toHTMLElement(root.querySelector('[data-bb-selected-bag]'));
 const selectedBagThumbEl = toHTMLElement(root.querySelector('[data-bb-selected-bag-thumb]'));
 const slotList = toHTMLElement(root.querySelector('[data-bb-slot-list]'));
 const originalPriceEl = toHTMLElement(root.querySelector('[data-bb-original-price]'));
 const savingEl = toHTMLElement(root.querySelector('[data-bb-saving]'));
 const kitPriceEl = toHTMLElement(root.querySelector('[data-bb-kit-price]'));
 const ctaButton = toHTMLButtonElement(root.querySelector('[data-bb-cta]'));
 const ctaNote = toHTMLElement(root.querySelector('[data-bb-cta-note]'));
 const errorEl = toHTMLElement(root.querySelector('[data-bb-error]'));

 if (!ctaButton || !slotList) {
   return;
 }
 const ctaButtonEl = ctaButton;
 const slotListEl = slotList;

 ctaButtonEl.textContent = resolvedCtaLabel;
 if (kitPriceEl) {
   kitPriceEl.textContent = formatMoneyMajorUnits(kitPrice);
 }

 bagCards.forEach((card) => {
   card.addEventListener('click', () => handleBagSelect(card));
   card.addEventListener('keydown', (event) => {
     const keyEvent = /** @type {KeyboardEvent} */ (event);
     if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
       keyEvent.preventDefault();
       handleBagSelect(card);
     }
   });
 });

 productCards.forEach((card) => {
   card.addEventListener('click', () => handleProductToggle(card));
   card.addEventListener('keydown', (event) => {
     const keyEvent = /** @type {KeyboardEvent} */ (event);
     if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
       keyEvent.preventDefault();
       handleProductToggle(card);
     }
   });
 });

 ctaButtonEl.addEventListener('click', () => {
   if (!isSelectionComplete() || isSubmitting) {
     return;
   }
   submitBundleToCart();
 });

 renderAll();

 /**
  * @param {BagItem | ProductItem | undefined} item
  * @returns {boolean}
  */
 function isItemAvailable(item) {
   if (!item) {
     return false;
   }
   return item.available !== false;
 }

 function sanitizeSelections() {
   if (selectedBag !== null && !isItemAvailable(bags[selectedBag])) {
     selectedBag = null;
   }
   selectedProducts = selectedProducts.filter((productIndex) => isItemAvailable(products[productIndex]));
 }

 /**
  * @param {Element} card
  */
 function handleBagSelect(card) {
   const index = toIndex(card.getAttribute('data-bb-index'));
   if (index === null || !bags[index] || !isItemAvailable(bags[index])) {
     return;
   }
   selectedBag = index;
   hideError();
   pushDataLayerEvent('bundle_bag_selected', { bag_name: bags[index].name });
   renderAll();
 }

 /**
  * @param {Element} card
  */
 function handleProductToggle(card) {
   if (selectedBag === null) {
     return;
   }

   const index = toIndex(card.getAttribute('data-bb-index'));
   if (index === null || !products[index] || !isItemAvailable(products[index])) {
     return;
   }

   const selectedIndex = selectedProducts.indexOf(index);
   if (selectedIndex > -1) {
     selectedProducts.splice(selectedIndex, 1);
     pushDataLayerEvent('bundle_product_removed', { product_title: products[index].title });
   } else if (selectedProducts.length < maxProducts) {
     selectedProducts.push(index);
     pushDataLayerEvent('bundle_product_added', { product_title: products[index].title });
   } else {
     return;
   }

   hideError();
   renderAll();
 }

 function renderAll() {
   sanitizeSelections();
   renderBags();
   renderProducts();
   renderSteps();
   renderProgress();
   renderSidebar();
   renderPricing();
   renderCta();
 }

 function renderBags() {
   bagCards.forEach((card) => {
     const index = toIndex(card.getAttribute('data-bb-index'));
     const unavailable = index !== null && !isItemAvailable(bags[index]);
     const isSelected = index === selectedBag && !unavailable;
     card.classList.toggle('bb-bag-card--selected', isSelected);
     card.classList.toggle('bb-bag-card--unavailable', unavailable);
     card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
     card.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
     if (card instanceof HTMLElement) {
       card.tabIndex = unavailable ? -1 : 0;
     }
   });
 }

 function renderProducts() {
   const hasBag = selectedBag !== null;
   const hasReachedLimit = selectedProducts.length === maxProducts;

   if (productGrid) {
     productGrid.hidden = !hasBag;
   }
   if (noBagNotice) {
     noBagNotice.hidden = hasBag;
   }
   if (productStepNumber) {
     productStepNumber.classList.toggle('bb-section-label-num--inactive', !hasBag);
   }

   productCards.forEach((card) => {
     const index = toIndex(card.getAttribute('data-bb-index'));
     const unavailable = index !== null && !isItemAvailable(products[index]);
     const isSelected = index !== null && selectedProducts.includes(index) && !unavailable;
     const isDisabled = hasBag && hasReachedLimit && !isSelected && !unavailable;
     const isLocked = !hasBag;
     card.classList.toggle('bb-prod-card--selected', isSelected);
     card.classList.toggle('bb-prod-card--disabled', isDisabled);
     card.classList.toggle('bb-prod-card--locked', isLocked);
     card.classList.toggle('bb-prod-card--unavailable', unavailable);
     card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
     card.setAttribute('aria-disabled', unavailable || isDisabled || isLocked ? 'true' : 'false');
     if (card instanceof HTMLElement) {
       card.tabIndex = unavailable || isLocked ? -1 : 0;
     }
   });
 }

 function renderSteps() {
   setStepActive(2, selectedBag !== null);
   setStepActive(3, isSelectionComplete());
 }

 function renderProgress() {
   const count = selectedProducts.length;
   const percent = Math.round((count / maxProducts) * 100);
   let message = tr('progress_choose_bag');

   if (selectedBag !== null) {
     message =
       count === maxProducts
         ? interpolate(tr('progress_all_selected'), { total: maxProducts })
         : interpolate(tr('progress_more_to_go'), { count: maxProducts - count });
   }

   if (progressFill) {
     progressFill.style.width = `${percent}%`;
   }
   if (progressCount) {
     progressCount.textContent = interpolate(tr('progress_count'), {
       current: count,
       total: maxProducts
     });
   }
   if (progressText) {
     progressText.textContent = message;
   }
 }

 function renderSidebar() {
   if (selectedBagThumbEl) {
     selectedBagThumbEl.innerHTML = '';
     if (selectedBag === null) {
       selectedBagThumbEl.classList.add('bb-slot-thumb--empty');
     } else {
       const bagData = bags[selectedBag];
       selectedBagThumbEl.classList.remove('bb-slot-thumb--empty');
       if (bagData && bagData.image) {
         appendSlotImage(selectedBagThumbEl, bagData.image, bagData.name);
       } else {
         selectedBagThumbEl.classList.add('bb-slot-thumb--empty');
       }
     }
   }

   if (selectedBagEl) {
     if (selectedBag === null) {
       selectedBagEl.textContent = tr('none_selected');
       selectedBagEl.classList.add('bb-sbp-name--empty');
     } else {
       const selectedBagData = bags[selectedBag];
       if (selectedBagData) {
         selectedBagEl.textContent = selectedBagData.name;
         selectedBagEl.classList.remove('bb-sbp-name--empty');
       }
     }
   }

   slotListEl.innerHTML = '';

   for (let slot = 0; slot < maxProducts; slot += 1) {
     const productIndex = selectedProducts[slot];
     const slotItem = document.createElement('div');
     slotItem.className = 'bb-slot-item';

     const thumb = document.createElement('div');
     thumb.className = 'bb-slot-thumb';

     const info = document.createElement('div');
     info.className = 'bb-slot-info';

     if (typeof productIndex === 'number' && products[productIndex]) {
       const selectedProduct = products[productIndex];
       appendSlotImage(thumb, selectedProduct.image, selectedProduct.title);

       const name = document.createElement('div');
       name.className = 'bb-slot-info-name';
       name.textContent = selectedProduct.title;

       const sub = document.createElement('div');
       sub.className = 'bb-slot-info-sub';
       sub.textContent = selectedProduct.subtitle || tr('subtitle_fallback');

       info.append(name, sub);

       const removeButton = document.createElement('button');
       removeButton.type = 'button';
       removeButton.className = 'bb-slot-remove';
       removeButton.setAttribute(
         'aria-label',
         interpolate(tr('remove_product_aria'), { product_title: selectedProduct.title })
       );
       removeButton.textContent = '×';
       removeButton.addEventListener('click', createSlotRemoveHandler(productIndex));

       slotItem.append(thumb, info, removeButton);
     } else {
       slotItem.classList.add('bb-slot-item--empty');
       thumb.classList.add('bb-slot-thumb--empty');

       const emptyName = document.createElement('div');
       emptyName.className = 'bb-slot-info-name bb-slot-info-name--empty';
       emptyName.textContent = interpolate(tr('slot_label'), { number: slot + 1 });

       const emptySub = document.createElement('div');
       emptySub.className = 'bb-slot-info-sub';
       emptySub.textContent = tr('slot_empty');

       info.append(emptyName, emptySub);
       slotItem.append(thumb, info);
     }

     slotListEl.appendChild(slotItem);
   }
 }

 function renderPricing() {
   const productTotal = selectedProducts.reduce((sum, productIndex) => {
     const product = products[productIndex];
     return sum + (product && typeof product.price === 'number' ? product.price : 0);
   }, 0);

   const selectedBagData = selectedBag !== null ? bags[selectedBag] : null;
   const bagTotal = selectedBagData && typeof selectedBagData.price === 'number' ? selectedBagData.price : 0;
   const individualTotal = bagTotal + productTotal;

   const saving = Math.max(0, individualTotal - kitPrice);

   if (originalPriceEl) {
     originalPriceEl.textContent = formatMoneyMajorUnits(individualTotal);
   }
   if (savingEl) {
     savingEl.textContent = saving > 0 ? `-${formatMoneyMajorUnits(saving)}` : tr('saving_none');
   }
 }

 function renderCta() {
   const complete = isSelectionComplete();
   const ariaLabel = complete
     ? interpolate(tr('cta_aria_ready'), { cta_label: resolvedCtaLabel, total: maxProducts })
     : interpolate(tr('cta_aria_disabled'), { total: maxProducts });

   ctaButtonEl.disabled = !complete || isSubmitting;
   ctaButtonEl.setAttribute('aria-disabled', ctaButtonEl.disabled ? 'true' : 'false');
   ctaButtonEl.setAttribute('aria-label', ariaLabel);

   if (ctaNote) {
     ctaNote.textContent = complete
       ? tr('cta_note_ready')
       : interpolate(tr('cta_note_incomplete'), { total: maxProducts });
   }
 }

 /**
  * @param {number} productIndex
  * @returns {() => void}
  */
 function createSlotRemoveHandler(productIndex) {
   return () => {
     const index = selectedProducts.indexOf(productIndex);
     if (index === -1) {
       return;
     }
     selectedProducts.splice(index, 1);
     if (products[productIndex]) {
       pushDataLayerEvent('bundle_product_removed', { product_title: products[productIndex].title });
     }
     hideError();
     renderAll();
   };
 }

 /**
  * @param {HTMLElement} container
  * @param {string | null} src
  * @param {string} alt
  */
 function appendSlotImage(container, src, alt) {
   if (!src) {
     return;
   }
   const image = document.createElement('img');
   image.loading = 'lazy';
   image.src = src;
   image.alt = alt || '';
   container.appendChild(image);
 }

 /**
  * @param {1|2|3} stepNumber
  * @param {boolean} isActive
  */
 function setStepActive(stepNumber, isActive) {
   const numberEl = stepNums[stepNumber];
   const labelEl = stepLabels[stepNumber];
   if (!numberEl || !labelEl) {
     return;
   }
   numberEl.classList.toggle('bb-step-num--inactive', !isActive);
   labelEl.classList.toggle('bb-step-label--inactive', !isActive);
 }

 /**
  * @returns {boolean}
  */
 function isSelectionComplete() {
   return (
     selectedBag !== null &&
     isItemAvailable(bags[selectedBag]) &&
     selectedProducts.length === maxProducts &&
     selectedProducts.every((productIndex) => isItemAvailable(products[productIndex]))
   );
 }

 /**
  * @returns {CartItem[]}
  */
 function buildCartItems() {
   /** @type {CartItem[]} */
   const items = [];
   if (selectedBag === null) {
     return items;
   }

   const bag = bags[selectedBag];
   if (!bag || !bag.variantId || !isItemAvailable(bag)) {
     return items;
   }

   const bagVariantId = bag.variantId;

   items.push({
     id: bagVariantId,
     quantity: 1,
     properties: {
       _bundle_id: bundleId,
       _bundle_role: 'bag'
     }
   });

   selectedProducts.forEach((productIndex) => {
     const product = products[productIndex];
     if (!product || !product.variantId || !isItemAvailable(product)) {
       return;
     }
     items.push({
       id: product.variantId,
       quantity: 1,
       // Nest travel sizes under the bag so checkout shows native parent/child lines.
       parent_id: bagVariantId,
       properties: {
         _bundle_id: bundleId,
         _bundle_role: 'product'
       }
     });
   });

   return items;
 }

 function submitBundleToCart() {
   const items = buildCartItems();
   const expectedLineItems = maxProducts + 1;
   if (items.length !== expectedLineItems) {
     showError(tr('error_items_unavailable'));
     return;
   }

   isSubmitting = true;
   hideError();
   renderCta();

   fetch('/cart/add.js', {
     method: 'POST',
     headers: {
       'Content-Type': 'application/json',
       Accept: 'application/json'
     },
     body: JSON.stringify({ items })
   })
     .then(assertResponseOk)
     .then(() =>
       fetch('/cart/update.js', {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           Accept: 'application/json'
         },
         body: JSON.stringify({
           attributes: {
             bundle_builder: true,
             bundle_builder_max_products: String(maxProducts),
           },
         })
       })
     )
     .then(assertResponseOk)
     .then(() => {
       pushDataLayerEvent('bundle_add_to_cart', { items, total_value: kitPrice });
       openCartOrRedirect();
     })
     .catch(() => {
       showError(tr('error_add_failed'));
     })
     .finally(() => {
       isSubmitting = false;
       renderCta();
     });
 }

 function openCartOrRedirect() {
   const drawer = document.querySelector('cart-drawer, #CartDrawer, [id*="CartDrawer"]');
   if (drawer) {
     document.dispatchEvent(new CustomEvent('cart:open'));
     return;
   }
   window.location.href = '/cart';
 }

 /**
  * @param {string} eventName
  * @param {Record<string, unknown>} payload
  */
 function pushDataLayerEvent(eventName, payload) {
   const win = /** @type {{ dataLayer?: DataLayerEvent[] }} */ (window);
   if (!Array.isArray(win.dataLayer)) {
     return;
   }
   win.dataLayer.push({ event: eventName, ...payload });
 }

 /**
  * @param {Response} response
  * @returns {Promise<unknown>}
  */
 function assertResponseOk(response) {
   if (!response || !response.ok) {
     throw new Error('Request failed');
   }
   return response.json();
 }

 /**
  * @param {string} message
  */
 function showError(message) {
   if (!errorEl) {
     return;
   }
   errorEl.textContent = message;
   errorEl.hidden = false;
 }

 function hideError() {
   if (!errorEl) {
     return;
   }
   errorEl.hidden = true;
   errorEl.textContent = '';
 }

 /**
  * @param {number | string | null | undefined} value
  * @returns {string}
  */
 function formatMoneyMajorUnits(value) {
   let numeric = typeof value === 'number' ? value : Number(value);
   if (!Number.isFinite(numeric)) {
     numeric = 0;
   }
   const cents = Math.round(numeric * 100);
   const formatStr = typeof config.moneyFormat === 'string' ? config.moneyFormat : '{{amount}}';
   const shopify = /** @type {{ formatMoney?: (cents: number, format: string) => string }} | undefined */ (
     typeof window.Shopify !== 'undefined' ? window.Shopify : undefined
   );
   if (shopify && typeof shopify.formatMoney === 'function') {
     try {
       return shopify.formatMoney(cents, formatStr);
     } catch {
       /* fall through */
     }
   }
   const code = typeof config.currencyCode === 'string' && config.currencyCode.length ? config.currencyCode : 'GBP';
   try {
     return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(numeric);
   } catch {
     return numeric.toFixed(2);
   }
 }

 /**
  * @param {string | null} value
  * @returns {number | null}
  */
 function toIndex(value) {
   const parsed = Number(value);
   if (!Number.isFinite(parsed)) {
     return null;
   }
   return parsed;
 }

 /**
  * @param {Element | null} element
  * @returns {HTMLElement | null}
  */
 function toHTMLElement(element) {
   return element instanceof HTMLElement ? element : null;
 }

 /**
  * @param {Element | null} element
  * @returns {HTMLButtonElement | null}
  */
 function toHTMLButtonElement(element) {
   return element instanceof HTMLButtonElement ? element : null;
 }

 /**
  * @returns {string}
  */
 function generateBundleId() {
   if (window.crypto && typeof window.crypto.randomUUID === 'function') {
     return window.crypto.randomUUID();
   }
   return `bb-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
 }
});
